import { v4 as uuidv4 } from 'uuid';

export interface AcceptanceFieldValue {
  id: string;
  value: any;
}

export interface AcceptanceRecordEntry {
  uuid: string;
  acceptance_record: AcceptanceFieldValue[];
  groups: AcceptanceFieldValue[];
}

// group_values keys owned by the acceptance-record feature. Everything else
// (e.g. sample-record data) passes through untouched.
const ACCEPTANCE_KEYS = ['acceptance_record', 'acceptance_records', 'groups'];

/**
 * Normalizes a material's group_values (string or object, legacy or new shape)
 * into a list of acceptance record entries plus the pass-through keys.
 *
 * Legacy shape { acceptance_record: [...], groups: [...] } becomes a single
 * entry in `records`. New shape { acceptance_records: [...] } is used as-is
 * (entries missing a uuid get a client-generated one).
 */
export function parseAcceptanceGroupValues(groupValues: any): {
  records: AcceptanceRecordEntry[];
  passthrough: { [key: string]: any };
} {
  let gv = groupValues;
  if (typeof gv === 'string') {
    try { gv = JSON.parse(gv); } catch { gv = {}; }
  }
  gv = gv || {};

  const passthrough: { [key: string]: any } = {};
  Object.keys(gv).forEach(key => {
    if (!ACCEPTANCE_KEYS.includes(key)) {
      passthrough[key] = gv[key];
    }
  });

  let records: AcceptanceRecordEntry[] = [];
  if (Array.isArray(gv.acceptance_records)) {
    records = gv.acceptance_records.map(r => ({
      uuid: r?.uuid || uuidv4(),
      acceptance_record: Array.isArray(r?.acceptance_record) ? [...r.acceptance_record] : [],
      groups: Array.isArray(r?.groups) ? [...r.groups] : [],
    }));
  } else if (
    (Array.isArray(gv.acceptance_record) && gv.acceptance_record.length > 0) ||
    (Array.isArray(gv.groups) && gv.groups.length > 0)
  ) {
    // Legacy single-record shape → record 1
    records = [{
      uuid: uuidv4(),
      acceptance_record: Array.isArray(gv.acceptance_record) ? [...gv.acceptance_record] : [],
      groups: Array.isArray(gv.groups) ? [...gv.groups] : [],
    }];
  }

  return { records, passthrough };
}

export function acceptanceRecordHasData(record: AcceptanceRecordEntry): boolean {
  return (record?.acceptance_record?.length || 0) > 0 || (record?.groups?.length || 0) > 0;
}

export function createEmptyAcceptanceRecord(): AcceptanceRecordEntry {
  return { uuid: uuidv4(), acceptance_record: [], groups: [] };
}
